// Typed wrappers for the /v1/merchants/:id/payouts endpoint.
//
// BFF rule (CLAUDE.md rule 1): all calls go through apiClient — the single
// axios instance that injects Authorization: Bearer <token> (in-memory) and
// X-Merchant-ID (localStorage-mirrored). Never construct a new axios instance.
//
// Money handling (CLAUDE.md rule 6): amount_minor is bigint end-to-end. The
// apiClient's transformRequest uses json-bigint to serialise bigint as a plain
// JSON integer on the wire; transformResponse parses with json-bigint so any
// integer that exceeds Number.MAX_SAFE_INTEGER returns as bigint (smaller ints
// stay as number, which is why the Raw types use `number | bigint`). The
// BigInt(...) wrappers below normalise both branches to bigint — they are a
// no-op when the input is already bigint.
import { apiClient } from "./client";

// ---------------------------------------------------------------------------
// Response interfaces
// ---------------------------------------------------------------------------

/**
 * RawPayout — the JSON shape as returned by axios after json-bigint parse.
 * amount_minor is `number | bigint`: a JSON integer that json-bigint upgrades
 * to bigint only when it would lose precision in f64. This type is only used
 * at the deserialization boundary inside listPayouts; callers always receive
 * Payout (amount_minor normalised to bigint).
 */
interface RawPayout {
  id: number;
  merchant_id: number;
  /** Nullable parent batch id — null for single-payout / M2M-API rows. */
  batch_id: number | null;
  request_id: string;
  status: "needs_approval" | "pending" | "success" | "failure";
  /** JSON integer (INR paise); `number` when safe, `bigint` past 2^53. */
  amount_minor: number | bigint;
  currency: string;
  txn_mode: number;
  provider_txn_id: string | null;
  utr_no: string | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  beneficiary_id?: number | null;
  beneficiary_name?: string | null;
  account_no?: string | null;
  ifsc?: string | null;
}

/**
 * Payout — the typed payout record exposed to UI consumers.
 *
 * amount_minor is bigint (INR paise) — CLAUDE.md rule 6. TypeScript will catch
 * any code that uses p.amount_minor directly in float arithmetic.
 * Display via fmtCcyMinor(p.amount_minor). Never parseFloat.
 */
export interface Payout {
  id: number;
  merchant_id: number;
  /**
   * Nullable parent batch id. Non-null for rows uploaded via CSV;
   * null for rows from the single-payout modal or the M2M API. Drives
   * batch-grouping on the /approvals queue so a 500-row CSV needs ONE
   * "Approve all" click, not 500.
   */
  batch_id: number | null;
  request_id: string;
  /** Live status enum from the backend — NOT the mock values. */
  status: "needs_approval" | "pending" | "success" | "failure";
  /** INR paise as bigint — CLAUDE.md rule 6. Never parseFloat. */
  amount_minor: bigint;
  currency: string;
  txn_mode: number;
  provider_txn_id: string | null;
  utr_no: string | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  /**
   * Whether POST :id/cancel would succeed, decided by the backend.
   *
   * Do NOT re-derive this in the panel. The rule depends on the rail type
   * and on whether an export has claimed the row, and a second copy in
   * TypeScript would drift the first time a rail is added — with the failure
   * being a Cancel button that appears to work on money already moving.
   * payout.CanCancelPayout is the one copy; this is its answer.
   *
   * Optional on the wire so older responses don't break the type; treat
   * undefined as false and hide the button rather than offering one that 409s.
   */
  can_cancel?: boolean;
  /**
   * Why cancelling is refused. Empty/absent when can_cancel is true.
   *   already_terminal   — succeeded or failed; nothing to stop
   *   in_flight_at_rail  — an integrated rail has it; only they can reverse it
   *   already_exported   — a file claimed it and may be at the bank; cancel
   *                        the export first, which frees the row
   */
  cancel_blocked_reason?: CancelBlockedReason | "";
  /** The export holding this payout. Set when cancel_blocked_reason is
   *  already_exported, so the panel can link straight to it. */
  export_id?: number | null;
  /**
   * Beneficiary identity fields stamped server-side from the bene
   * row matching beneficiary_id (single merchant-scoped lookup per
   * list request, no N+1). Null/undefined when:
   *   - beneficiary_id is null (rare: legacy or pending-bene-register row)
   *   - the bene row was deleted before this read landed
   *   - the backend skipped enrichment (lookup error)
   * Panel renders "—" when missing.
   */
  beneficiary_id?: number | null;
  beneficiary_name?: string | null;
  account_no?: string | null;
  ifsc?: string | null;
}

// ---------------------------------------------------------------------------
// API function
// ---------------------------------------------------------------------------

/**
 * PayoutListParams — the filters GET /v1/merchants/:id/payouts applies in
 * SQL. Every one is optional. from/to are RFC 3339 instants ([from, to)),
 * produced by dateRangeBounds so the list and the dashboard window agree.
 */
export interface PayoutListParams {
  status?: string;
  from?: string;
  to?: string;
  q?: string;
  txn_mode?: number;
  min_amount_minor?: bigint;
  sort?: "newest" | "oldest";
  limit?: number;
  offset?: number;
}

/** The server caps one page at this many rows. */
export const PAYOUT_PAGE_MAX = 500;

/**
 * PayoutListPage — one page of rows plus aggregates over EVERY row the
 * filter matches. Print `total` / `amountMinorByCurrency`, never a sum of
 * `payouts`: the page is a slice, and summing it is how this screen once
 * reported 481 of a merchant's 588 settled payouts as the whole.
 */
export interface PayoutListPage {
  payouts: Payout[];
  total: number;
  amountMinorByCurrency: Record<string, bigint>;
  /** Count per status over the filter WITHOUT its status (for the tabs). */
  statusCounts: Record<string, number>;
  /** status → currency → paise, over the same rows as statusCounts. */
  statusAmountsMinor: Record<string, Record<string, bigint>>;
  limit: number;
  offset: number;
}

interface RawPayoutListPage {
  payouts: RawPayout[];
  total: number;
  amount_minor_by_currency: Record<string, number | bigint>;
  status_counts: Record<string, number>;
  status_amounts_minor: Record<string, Record<string, number | bigint>>;
  limit: number;
  offset: number;
}

function toBigintMap(m: Record<string, number | bigint> | null | undefined): Record<string, bigint> {
  const out: Record<string, bigint> = {};
  for (const [k, v] of Object.entries(m ?? {})) out[k] = BigInt(v);
  return out;
}

/**
 * listPayouts — GET /v1/merchants/:merchantId/payouts
 * One filtered page of the merchant's payouts, with true totals. Requires
 * viewer+ membership.
 *
 * Normalises every amount to bigint at the deserialization boundary
 * (CLAUDE.md rule 6 — money is bigint end-to-end in the SPA).
 */
export async function listPayouts(
  merchantId: number,
  params: PayoutListParams = {},
): Promise<PayoutListPage> {
  const resp = await apiClient.get<RawPayoutListPage>(
    `/v1/merchants/${merchantId}/payouts`,
    {
      params: {
        ...params,
        min_amount_minor: params.min_amount_minor?.toString(),
      },
    },
  );
  const d = resp.data;
  const statusAmountsMinor: Record<string, Record<string, bigint>> = {};
  for (const [st, m] of Object.entries(d.status_amounts_minor ?? {})) {
    statusAmountsMinor[st] = toBigintMap(m);
  }
  return {
    payouts: (d.payouts ?? []).map((p) => ({
      ...p,
      amount_minor: BigInt(p.amount_minor),
    })),
    total: d.total ?? 0,
    amountMinorByCurrency: toBigintMap(d.amount_minor_by_currency),
    statusCounts: d.status_counts ?? {},
    statusAmountsMinor,
    limit: d.limit,
    offset: d.offset,
  };
}

/**
 * listAllPayouts pages through every row matching `params` — for CSV
 * export, which must contain what the header total counts, not one page.
 */
export async function listAllPayouts(
  merchantId: number,
  params: Omit<PayoutListParams, "limit" | "offset">,
): Promise<Payout[]> {
  const out: Payout[] = [];
  for (let offset = 0; ; offset += PAYOUT_PAGE_MAX) {
    const page = await listPayouts(merchantId, { ...params, limit: PAYOUT_PAGE_MAX, offset });
    out.push(...page.payouts);
    if (page.payouts.length < PAYOUT_PAGE_MAX || out.length >= page.total) return out;
  }
}

/**
 * CreatePayoutPayload — POST /v1/merchants/:id/payouts body.
 *
 * amount_minor is INR paise as bigint — CLAUDE.md rule 6. The apiClient's
 * transformRequest (json-bigint stringify) serialises it as a plain JSON
 * integer on the wire; do NOT coerce with Number() at the call site.
 * request_id is the caller-supplied idempotency key.
 */
export interface CreatePayoutPayload {
  bank_account_id: number;
  beneficiary_id: number;
  amount_minor: bigint;
  txn_mode: number;
  request_id: string;
}

/**
 * createPayout — POST /v1/merchants/:merchantId/payouts
 * Initiates a single payout (admin-only). Returns the created payout — or, on
 * an idempotent replay of request_id, the original row.
 */
export async function createPayout(
  merchantId: number,
  payload: CreatePayoutPayload,
): Promise<Payout> {
  const resp = await apiClient.post<RawPayout>(
    `/v1/merchants/${merchantId}/payouts`,
    payload,
  );
  return { ...resp.data, amount_minor: BigInt(resp.data.amount_minor) };
}

/**
 * ForceRepollResult — response shape for the admin repair endpoint.
 *
 * repaired=true   → the row was terminal, got reset to pending, and CheckStatus
 *                   was called inline. `payout` reflects whatever upstream
 *                   reported on that single round-trip.
 * repaired=false  → the row was already pending (idempotent no-op). `hint`
 *                   echoes the backend message ("reconciler will repoll on its
 *                   next sweep").
 */
export interface ForceRepollResult {
  payout: Payout;
  repaired: boolean;
  hint?: string;
}

/**
 * forceRepollPayout — POST /v1/admin/payouts/:id/force-repoll
 *
 * Admin-only repair path for payouts wrongly marked terminal (e.g. a IDFC
 * response that read as failure but actually means "still pending at the
 * network end"). Bypasses D-04 first-terminal-write-wins deliberately and
 * every call is audited under action="payout.admin_repair" on the backend.
 *
 * Use sparingly. The non-repair path is the per-merchant Refresh endpoint
 * (POST /v1/merchants/:id/payouts/:pid/refresh) which works on pending rows.
 */
export async function forceRepollPayout(
  payoutId: number,
): Promise<ForceRepollResult> {
  const resp = await apiClient.post<{
    payout: RawPayout;
    repaired: boolean;
    hint?: string;
  }>(`/v1/admin/payouts/${payoutId}/force-repoll`);
  return {
    payout: {
      ...resp.data.payout,
      amount_minor: BigInt(resp.data.payout.amount_minor),
    },
    repaired: resp.data.repaired,
    hint: resp.data.hint,
  };
}

// ─── Admin approval queue (migration 019) ────────────────────────────────────

/**
 * listNeedsApproval — GET /v1/admin/payouts/needs-approval
 *
 * Returns every payout currently in 'needs_approval' status. Admin-only at
 * the route layer (RequireRole(admin)); the API client doesn't gate calls
 * — the backend returns 403 for non-admins.
 *
 * Optional merchantId scopes the queue to a single merchant. Omit to see
 * the cross-tenant queue (the admin's natural workflow).
 *
 * amount_minor normalised to bigint at the deserialization boundary
 * (CLAUDE.md rule 6).
 */
export async function listNeedsApproval(merchantId?: number): Promise<Payout[]> {
  const qs = merchantId != null && merchantId > 0 ? `?merchant_id=${merchantId}` : "";
  const resp = await apiClient.get<{ payouts: RawPayout[] }>(
    `/v1/admin/payouts/needs-approval${qs}`,
  );
  return resp.data.payouts.map((p) => ({
    ...p,
    amount_minor: BigInt(p.amount_minor),
  }));
}

/**
 * approvePayout — POST /v1/admin/payouts/:id/approve
 *
 * Transitions the row from needs_approval → pending and dispatches to the
 * provider. Returns the updated row, including provider_txn_id once the
 * upstream call lands. On a transient provider error the row stays
 * 'pending' and the reconciler resolves it.
 *
 * Server errors:
 *   404 payout_not_found       — bad id
 *   409 payout_not_approvable  — concurrent admin won, or row already
 *                                left needs_approval state
 *   500 approve_dispatch       — transition committed but provider call
 *                                failed; reconciler will catch up
 */
export async function approvePayout(payoutId: number): Promise<Payout> {
  const resp = await apiClient.post<RawPayout>(
    `/v1/admin/payouts/${payoutId}/approve`,
  );
  return { ...resp.data, amount_minor: BigInt(resp.data.amount_minor) };
}

/**
 * rejectPayout — POST /v1/admin/payouts/:id/reject
 *
 * Transitions the row from needs_approval → failure with error_code=
 * 'admin_rejected' and rejection_reason set. No provider call.
 *
 * Reason is required by the backend (binding:required,min=1,max=500);
 * keep the panel's UX in lockstep so support tickets aren't a black box.
 *
 * Server errors:
 *   400 bad_request            — reason missing or > 500 chars
 *   404 payout_not_found       — bad id
 *   409 payout_not_approvable  — concurrent admin already acted
 */
export async function rejectPayout(payoutId: number, reason: string): Promise<Payout> {
  const resp = await apiClient.post<RawPayout>(
    `/v1/admin/payouts/${payoutId}/reject`,
    { reason },
  );
  return { ...resp.data, amount_minor: BigInt(resp.data.amount_minor) };
}

/**
 * CancelBlockedReason — the backend's stable tokens for "why not".
 * Kept as tokens rather than sentences because each one asks the operator for
 * something different, and that copy belongs here, next to the UI that shows it.
 */
export type CancelBlockedReason =
  | "already_terminal"
  | "in_flight_at_rail"
  | "already_exported";

/** Operator-facing copy for each refusal. */
export function cancelBlockedMessage(
  reason: CancelBlockedReason | "" | undefined,
  exportId?: number | null,
): string {
  switch (reason) {
    case "already_terminal":
      return "This payout has already finished — there is nothing left to stop.";
    case "in_flight_at_rail":
      return "This payout has been sent to the payout rail. It can no longer be cancelled here; the rail owns it now.";
    case "already_exported":
      return exportId
        ? `A payment file (export #${exportId}) has already claimed this payout, so it may be at the bank. Cancel that export first — that releases its rows and makes this cancellable again.`
        : "A payment file has already claimed this payout. Cancel that export first to release it.";
    default:
      return "This payout cannot be cancelled.";
  }
}

/**
 * cancelPayout — POST /v1/admin/payouts/:id/cancel
 *
 * Stops a payout that has not left the system. Broader than rejectPayout,
 * which only ever touches a row in needs_approval: a payout sitting in
 * `pending` on a file rail was dispatched to nobody and is still stoppable
 * until an export claims it.
 *
 * Throws on 409 payout_not_cancellable — read `reason` off the response body
 * and render it with cancelBlockedMessage.
 */
export async function cancelPayout(
  payoutId: number,
  reason: string,
  /**
   * force stops a payout the safe path refuses — already at the rail, or
   * already claimed by a payment file. It changes OUR record only; the rail
   * may still pay. Send it only after the operator has been shown that and
   * confirmed. The backend downgrades to a normal cancel when the safe path
   * would have worked, so force never makes a clean stop look like an
   * override.
   */
  force = false,
): Promise<Payout> {
  const resp = await apiClient.post<RawPayout>(
    `/v1/admin/payouts/${payoutId}/cancel`,
    { reason, force },
  );
  return { ...resp.data, amount_minor: BigInt(resp.data.amount_minor) };
}

/** CancelBatchResult — what a whole-batch cancel could and could not touch. */
export interface CancelBatchResult {
  batch_id: number;
  cancelled_count: number;
  cancelled_ids: number[];
  /** payout id (as a string key) → why it could not be cancelled. */
  blocked: Record<string, CancelBlockedReason>;
  blocked_count: number;
}

/**
 * cancelBatch — POST /v1/admin/payouts/batches/:batchId/cancel
 *
 * Always succeeds when the batch exists, even if it cancelled nothing: a batch
 * whose rows have partly gone out is an answer, not an error, and the per-row
 * reasons are what the operator needs to read it.
 */
export async function cancelBatch(
  batchId: number,
  reason: string,
): Promise<CancelBatchResult> {
  const resp = await apiClient.post<CancelBatchResult>(
    `/v1/admin/payouts/batches/${batchId}/cancel`,
    { reason },
    // Same per-call override as approveBatch/rejectBatch: one DB transaction
    // per row means a 500-row batch beats the 15s axios default.
    { timeout: 300_000 },
  );
  return resp.data;
}

/** TestNotificationResult — response shape for the admin debug endpoint. */
export interface TestNotificationResult {
  configured: boolean;
  sent: boolean;
  error?: string;
}

/**
 * testNotification — POST /v1/admin/notify/test
 *
 * Fires a single dummy approval-queue email via the configured Postal
 * notifier. Lets an admin verify POSTAL_URL / POSTAL_API_KEY /
 * ADMIN_NOTIFY_EMAILS without uploading a real CSV.
 *
 *   configured=false  → Postal env not set; no email sent. Operator
 *                       needs to set the env vars and restart payouts-api.
 *   configured=true, sent=true   → email accepted by Postal API.
 *   configured=true, sent=false  → Postal returned an error; `error`
 *                                  carries the diagnostic excerpt.
 */
export async function testNotification(): Promise<TestNotificationResult> {
  const resp = await apiClient.post<TestNotificationResult>(
    `/v1/admin/notify/test`,
  );
  return resp.data;
}
